import { Box, DashboardContainer, IUserSummary, useCommonComponentContext } from "@maysoft/common-component-react";
import { useMemo } from "react";

import Strings from "constants/strings";

import { BarChart } from "@mui/x-charts/BarChart";
import { ICodename } from "commons/interfaces";
import { DashboardLayout } from "layout";
import Constants from "constants/index";
import { Card, Typography, CardContent, useMediaQuery } from "@mui/material";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";

const DashboardScreen = () => {
    const { getResourcePermissions } = useCommonComponentContext();
    const resourcePermissions = getResourcePermissions(Constants.ResourceMenu.DASHBOARD);

    return (
        <DashboardLayout isPermission title={Strings.DASHBOARD.TITLE_MENU} route={[{ title: Strings.DASHBOARD.TITLE_MENU, route: "" }]}>
            <DashboardContainer RenderChart={RenderChart} />
        </DashboardLayout>
    );
};

const getSoftColor = (index: number) => {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue},40%,45%)`;
};

const RenderChart = ({
    data,
    serviceCodes
}: {
    data: IUserSummary["serviceSummary"];
    serviceCodes: ICodename[];
}) => {
    const chartData = useMemo(() => {
        return serviceCodes
            .map((service) => {
                const serviceData = data?.[service.code];

                return {
                    name: service.name,
                    value: Number(serviceData?.new ?? 0)
                };
            })
            .sort((a, b) => b.value - a.value);
    }, [data, serviceCodes]);

    const hasData = useMemo(() => {
        return data != null && chartData.length > 0;
    }, [data, serviceCodes, chartData]);

    const isMobile = useMediaQuery("(max-width:600px)");

    return (
        <Card sx={{ width: "100%", p: 0 }}>
            <CardContent>
                {hasData ?
                    <BarChart
                        layout="horizontal"
                        height={Math.max((chartData || []).length, 1) * 40 + 40}
                        series={[
                            {
                                data:
                                    chartData.length > 0
                                        ? chartData.map((i) => Number(i.value) || 0)
                                        : [0]
                            }
                        ]}
                        yAxis={[
                            {
                                data:
                                    chartData.length > 0
                                        ? chartData.map((i) => i.name)
                                        : ["No data"],
                                scaleType: "band"
                            }
                        ]}
                        xAxis={[
                            {
                                scaleType: "linear",
                                tickMinStep: 1,
                                tickLabelStyle: {
                                    fontSize: 12
                                }
                            }
                        ]}
                        margin={{ top: 10, right: 30, bottom: 30, left: isMobile ? 50 : 80 }}
                        sx={{
                            ".MuiBarElement-root": {
                                rx: 4,
                                ry: 4
                            },

                            // ".MuiChartsAxis-line": {
                            //     stroke: "#d1d5db",
                            //     strokeDasharray: "4 4"
                            // },
                            ".MuiChartsAxis-tick": {
                                stroke: "#616875",
                                strokeWidth: 1,
                                // transform: "scaleY(0.8)"
                            },
                            ".MuiBarLabel-root": {
                                fill: "#374151",
                                fontSize: 12,
                                fontWeight: 600
                            },
                            ...Object.fromEntries(
                                chartData.map((_, i) => [
                                    `.MuiBarElement-root:nth-of-type(${i + 1})`,
                                    { fill: getSoftColor(i) }
                                ])
                            )
                        }}
                    />
                    : <EmptyChart />}
            </CardContent>
        </Card>
    );
};

const EmptyChart = () => {
    return (
        <Box
            height={300}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{
                borderRadius: 2
            }}
        >
            <InsertChartOutlinedIcon
                sx={{
                    fontSize: 48,
                    mb: 1,
                    color: "text.disabled",
                }}
            />

            <Typography variant="subtitle1" fontWeight={600}>
                {Strings.DASHBOARD.NO_CHART_DATA}
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ maxWidth: 220 }}
            >
                {Strings.DASHBOARD.NO_CHART_DATA_DESCRIPTION}
            </Typography>
        </Box>
    );
};

export default DashboardScreen;
